<template>
    <v-container>
        <v-row>
            <v-col cols="4">
                <v-form ref="form" :disabled="!record.userCanSave(user)" @input="$emit('input', $event)">
                    <v-switch
                        :input-value="record.ak_durchgefuert"
                        :label="`${$t('event_durchgefuert')}: ${textSwitchState}`"
                        :color="record.ak_durchgefuert ? 'success' : ''"
                        @change="updateRecord('ak_durchgefuert', $event)"
                    />
                    <LabelField :label="$t('remark')" class="mb-2">
                        <v-textarea
                            dense
                            outlined
                            hide-details="auto"
                            :placeholder="$t('placeholder_remark')"
                            :value="record.ak_bemerkung"
                            @input="updateRecord('ak_bemerkung', $event)"
                        />
                    </LabelField>
                    <LabelField :label="$t('paid_out_fee')">
                        <v-text-field
                            :value="record.ak_ausbezahlte_gage"
                            dense
                            outlined
                            hide-details="auto"
                            type="number"
                            :placeholder="$t('placeholder_paid_out_fee')"
                            @input="updateRecord('ak_ausbezahlte_gage', $event)"
                        />
                    </LabelField>
                </v-form>
            </v-col>
            <v-col cols="8">
                <v-data-table :headers="headers" :items="belege" item-key="id" dense>
                    <template #top>
                        <v-toolbar flat>
                            <v-spacer />
                            <v-dialog v-model="dialogNew" persistent max-width="500px">
                                <template #activator="{ on, attrs }">
                                    <IconBtn
                                        v-bind="attrs"
                                        color="gray"
                                        icon-cls="upload"
                                        icon-color="black"
                                        :tooltip="$t('add_new_receipt')"
                                        v-on="on"
                                    />
                                </template>
                                <v-card>
                                    <v-card-title>
                                        <span class="text-h5">{{ $t('new_receipt') }}</span>
                                    </v-card-title>

                                    <v-card-text>
                                        <v-container>
                                            <v-row>
                                                <v-col cols="12">
                                                    <UploadCard
                                                        api-endpoint="/EventBeleg/flowUpload"
                                                        :allowed-types="allowedFileTypes"
                                                        :upload-params="{ event_id: record.getId() }"
                                                        :single="false"
                                                        width="100%"
                                                        @uploaded="afterFilesUploaded"
                                                    />
                                                </v-col>
                                            </v-row>
                                        </v-container>
                                    </v-card-text>

                                    <v-card-actions>
                                        <v-spacer></v-spacer>
                                        <v-btn color="primary" text @click="closeDialogNew">
                                            {{ $t('schliessen') }}
                                        </v-btn>
                                    </v-card-actions>
                                </v-card>
                            </v-dialog>
                            <ConfirmDialog
                                :value="dialogConfirm"
                                :title="`${$t('delete')}`"
                                :context="`${$t('beleg')} ${editedItem.given_name}`"
                                :message="`${$t('delete_beleg_message', editedItem)}`"
                                @confirm="confirmDelete"
                                @cancel="cancelConfirm"
                            />
                        </v-toolbar>
                    </template>
                    <template #item.actions="{ item, index }">
                        <IconBtn icon-color="black" icon-cls="download" @click="downloadItem(item)" />
                        <IconBtn icon-color="black" icon-cls="trash" @click="deleteItem(item, index)" />
                    </template>
                </v-data-table>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import { Event } from '@/models/Event';
import { EventBeleg } from '@/models/EventBeleg';
import { ref } from 'vue';
import { instance as api } from '@/helpers/api';
import { getI18nInstance as i18n } from '@/i18n';
import LabelField from '@/components/form/LabelField.vue';
import UploadCard from '@/components/UploadCard.vue';
import { commitToast } from '@/plugins/toast';
import ConfirmDialog from '@/components/ConfirmDialog.vue';
import { mapState } from 'vuex';
import { apiUrl } from '@/helpers/globals';

export default {
    name: 'EventGage',
    components: {
        LabelField,
        UploadCard,
        ConfirmDialog,
    },
    props: {
        record: {
            type: Event,
            required: true,
        },
    },
    setup() {
        const belege = ref([]);

        const dialogNew = ref(false);
        const dialogConfirm = ref(false);
        const allowedFileTypes = ref(
            'image/*,.pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/msexcel,application/vnd.oasis.opendocument.spreadsheet',
        );
        const editedIndex = ref(-1);
        const editedItem = ref(new EventBeleg());
        const defaultItem = new EventBeleg();

        const headers = [
            { text: i18n().t('file_type'), sortable: true, value: 'readable_type' },
            { text: i18n().t('given_name'), sortable: true, value: 'name' },
            { text: i18n().t('size'), sortable: true, value: 'size' },
            { text: i18n().t('creator'), sortable: true, value: 'creator' },
            { sortable: false, align: 'center', value: 'actions' },
        ];

        return {
            belege,
            dialogNew,
            dialogConfirm,
            allowedFileTypes,
            editedIndex,
            editedItem,
            defaultItem,
            headers,
        };
    },
    computed: {
        ...mapState('user', {
            user: 'record',
        }),
        textSwitchState() {
            return `${this.record.ak_durchgefuert ? this.$t('yes') : this.$t('no')}`;
        },
    },
    async mounted() {
        this.validateForm();
        await this.loadBelegeForEvent();
    },
    methods: {
        async loadBelegeForEvent() {
            try {
                const event_id = this.record.getId();

                const response = await api().get('/EventBeleg/list', {
                    params: { event_id },
                });

                if (response?.data) {
                    this.belege = response.data.records;
                }
            } catch (e) {
                // pass
            }
        },
        async confirmDelete() {
            const response = await api().delete('/EventBeleg/destroy', {
                params: {
                    id: this.editedItem.id,
                },
            });
            if (response.data.success) {
                this.belege.splice(this.editedIndex, 1);
                const message = `${this.$t('success_delete')}`;
                commitToast({ message, color: 'success' });
            }
            this.cancelConfirm();
        },
        deleteItem(item, index) {
            this.editedItem = item;
            this.editedIndex = index;
            this.dialogConfirm = true;
        },
        cancelConfirm() {
            this.editedItem = this.defaultItem;
            this.editedIndex = -1;
            this.dialogConfirm = false;
        },
        async afterFilesUploaded() {
            await this.loadBelegeForEvent();
            this.$emit('save');
        },
        closeDialogNew() {
            this.dialogNew = false;
        },
        validateForm() {
            this.$refs.form.validate();
        },
        updateRecord(field, value) {
            this.record.set(field, value);
        },
        async downloadItem(item) {
            const url = apiUrl('/EventBeleg/download', {
                event_id: this.record.getId(),
                document_id: item.document_id,
            });
            window.open(url, item.name);
        },
    },
};
</script>

<style scoped lang="scss"></style>
